import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Save, ArrowLeft, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { useDemoTools } from '../hooks/useDemoTools';
import { toast } from 'sonner';
import { PageShell } from '../components/layout/PageShell';
import { PageHeroCompact } from '../components/layout/PageHeroCompact';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { SignInSecurityCard } from '../components/profile/SignInSecurityCard';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateProfile, logout, loading } = useAuth();
  const [demoTools, setDemoTools] = useDemoTools();
  
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [profile, setProfile] = useState({
    notifications: { email: true, lei_alerts: true, weekly_reports: false }
  });

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setProfile(prev => ({
        ...prev,
        ...user.profile,
        notifications: {
          ...prev.notifications,
          ...user.profile?.notifications
        }
      }));
    }
  }, [user]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateProfile({ name, profile });
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/login');
  };

  const __pageTheme = getLightSectionTheme('Admin');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHeroCompact title={`My Account`} description={`Manage your personal profile and notifications.`} theme={__pageTheme} />
    )}>
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
                  </div>

        <Tabs defaultValue="account" className="space-y-6">
          <TabsList className="w-auto">
            <TabsTrigger value="account" className="gap-2" data-testid="account-tab">
              <User className="h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2" data-testid="notifications-tab">
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
          </TabsList>

          {/* Account Tab */}
          <TabsContent value="account" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-teal-600" />
                  Account Information
                </CardTitle>
                <CardDescription>Your personal account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                      <User className="h-8 w-8 text-teal-600" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-slate-800">{user.name}</p>
                    <p className="text-sm text-slate-500">{user.email}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {user.auth_provider === 'google' ? 'Google Account' : 'Email Account'}
                    </p>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Full Name</Label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1"
                    data-testid="profile-name-input"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Email</Label>
                  <Input
                    type="email"
                    value={user.email}
                    disabled
                    className="mt-1 bg-slate-50"
                  />
                  <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid="profile-demo-tools-row">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Demo tools &amp; guided tour</p>
                    <p className="text-xs text-slate-500">Show the &ldquo;Tools&rdquo; launcher, the 60-second product tour, and the demo-share pills across the app.</p>
                  </div>
                  <Switch
                    checked={demoTools}
                    onCheckedChange={setDemoTools}
                    data-testid="profile-demo-tools-switch"
                  />
                </div>

                <div className="pt-4 border-t">
                  <Button
                    variant="destructive"
                    onClick={handleLogout}
                    data-testid="logout-btn"
                  >
                    Sign Out
                  </Button>
                </div>
              </CardContent>
            </Card>
            <SignInSecurityCard />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5 text-teal-600" />
                  Notification Preferences
                </CardTitle>
                <CardDescription>Manage how you receive notifications</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Email Notifications</p>
                    <p className="text-xs text-slate-500">Receive email updates for alerts and reports</p>
                  </div>
                  <Checkbox
                    checked={profile.notifications?.email !== false}
                    onCheckedChange={(checked) => setProfile({
                      ...profile,
                      notifications: { ...profile.notifications, email: checked }
                    })}
                    data-testid="notif-email-checkbox"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-700">LEI Alert Notifications</p>
                    <p className="text-xs text-slate-500">Get notified when LEI scores cross thresholds</p>
                  </div>
                  <Checkbox
                    checked={profile.notifications?.lei_alerts !== false}
                    onCheckedChange={(checked) => setProfile({
                      ...profile,
                      notifications: { ...profile.notifications, lei_alerts: checked }
                    })}
                    data-testid="notif-lei-alerts-checkbox"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Weekly Summary Reports</p>
                    <p className="text-xs text-slate-500">Receive a weekly digest of key metrics</p>
                  </div>
                  <Checkbox
                    checked={profile.notifications?.weekly_reports || false}
                    onCheckedChange={(checked) => setProfile({
                      ...profile,
                      notifications: { ...profile.notifications, weekly_reports: checked }
                    })}
                    data-testid="notif-weekly-checkbox"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Save Button */}
        <div className="flex justify-end mt-6 gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            data-testid="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="save-profile-btn"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            ) : (
              <>
                <Save className="mr-2 h-5 w-5" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
    </PageShell>
  );
};

export default ProfilePage;

